/**
 * FavoriteCard — single favorite meal row inside FavoriteMealsModal.
 *
 * Collapsed (default): meal-type icon + name + calories pill on the
 * top row, description (line-clamp-2) on the second row, chevron and
 * kebab on the right. Tapping the chevron toggles expansion; tapping
 * the kebab opens an Edit / Delete menu.
 *
 * Expanded: same header plus a stacked label/value list with the
 * three macros and the category (if any), and a usage-count line.
 *
 * The "Add to today" button stays prominent on the right of the top
 * row so the primary action is always reachable in one tap.
 */
import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  TagIcon,
  PlusIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'

import { formatCalories, formatGrams } from '../../../utils/format'

export default function FavoriteCard({
  favorite,
  displayName,
  expanded = false,
  onToggleExpand,
  onAdd,
  onEdit,
  onDelete,
  isAdding = false,
  addLabel,
  isManualSort = false,
  isDragging = false,
  isDragOver = false,
  draggableProps = {},
  mealTypeIcon = '🍽️',
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'en'

  const calories = favorite.total_calories ?? 0
  const protein = favorite.total_protein_g ?? 0
  const carbs = favorite.total_carbs_g ?? 0
  const fat = favorite.total_fat_g ?? 0
  const usedCount = favorite.times_used ?? 0

  return (
    <div
      {...draggableProps}
      className={`border rounded-lg transition-all ${
        isDragOver
          ? 'border-indigo-400 bg-indigo-50'
          : isDragging
          ? 'opacity-50 border-gray-300'
          : 'border-gray-200 hover:shadow-sm bg-surface-container'
      } ${isManualSort ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          {isManualSort && (
            <div
              className="flex-shrink-0 pt-1 text-gray-300 touch-none"
              aria-hidden="true"
            >
              <Bars3Icon className="h-5 w-5" />
            </div>
          )}

          <span
            className="text-2xl flex-shrink-0 pt-0.5"
            aria-hidden="true"
          >
            {mealTypeIcon}
          </span>

          <div className="flex-1 min-w-0">
            {/* Top row: name + calories + actions */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p
                  className="font-medium text-gray-900 truncate text-base"
                  title={displayName}
                >
                  {displayName}
                </p>
                <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">
                  {formatCalories(calories, locale)}{' '}
                  {t('nutrition.units.kcal', 'kcal')}
                </span>
              </div>

              <div className="flex items-center flex-shrink-0">
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  aria-label={
                    expanded
                      ? t('nutrition.actions.collapseMacros', 'Hide macros')
                      : t('nutrition.actions.expandMacros', 'Show macros')
                  }
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  {expanded ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>

                <Menu as="div" className="relative">
                  <Menu.Button
                    className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                    aria-label={t('common.more', 'More actions')}
                  >
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-1 w-40 origin-top-right rounded-md bg-surface-container shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20">
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={onEdit}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                                active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                              }`}
                            >
                              <PencilIcon className="h-4 w-4" />
                              {t('nutrition.actions.edit', 'Edit')}
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={onDelete}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 ${
                                active ? 'bg-red-50' : ''
                              }`}
                            >
                              <TrashIcon className="h-4 w-4" />
                              {t('nutrition.actions.delete', 'Delete')}
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>

            {/* Description row (collapsed view) */}
            {favorite.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {favorite.description}
              </p>
            )}

            {/* Add-to-today action */}
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={onAdd}
                disabled={isAdding}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-on-neutral bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50 min-h-[44px]"
                title={addLabel}
              >
                {isAdding ? (
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4" />
                    <span>{addLabel}</span>
                  </>
                )}
              </button>
            </div>

            {/* Expanded body */}
            {expanded && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                <MacroLine
                  label={t('nutrition.macros.protein', 'Protein')}
                  value={`${formatGrams(protein, locale)} ${t('nutrition.units.g', 'g')}`}
                />
                <MacroLine
                  label={t('nutrition.macros.carbs', 'Carbs')}
                  value={`${formatGrams(carbs, locale)} ${t('nutrition.units.g', 'g')}`}
                />
                <MacroLine
                  label={t('nutrition.macros.fat', 'Fat')}
                  value={`${formatGrams(fat, locale)} ${t('nutrition.units.g', 'g')}`}
                />
                {favorite.category && (
                  <MacroLine
                    label={t('favorites.category', 'Category')}
                    value={
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-600">
                        <TagIcon className="h-3 w-3 mr-0.5" />
                        {favorite.category}
                      </span>
                    }
                  />
                )}
                <p className="text-xs text-gray-400 pt-1">
                  {usedCount > 0
                    ? `${usedCount}x ${t('favorites.used', 'used')}`
                    : t('favorites.neverUsed', 'Never used')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function MacroLine({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 tabular-nums">{value}</span>
    </div>
  )
}
